import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, RefreshCw, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { confirm } from "@/lib/confirm";
import { safeBRL, fmtDate, fmtDateTime } from "@/lib/formatters";

/** AI weekly summary card with on-demand generation (consumes Emergent LLM credits). */
export default function WeeklySummaryCard() {
  const [weekly, setWeekly] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/ai/weekly-summary/latest")
      .then((r) => setWeekly(r.data || null))
      .catch(() => setWeekly(null));
  }, []);

  async function generate(regen = false) {
    if (loading) return;
    if (regen && !(await confirm({ description: "Gerar um novo resumo consome créditos da chave Emergent. Continuar?" }))) return;
    setLoading(true);
    try {
      const r = await api.post("/ai/weekly-summary", {});
      setWeekly(r.data);
      toast.success("Resumo gerado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="weekly-summary-card">
      <CardContent className="p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-blue-700" /> Resumo semanal por IA
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {weekly
                ? <>Período {fmtDate(weekly.date_from)} – {fmtDate(weekly.date_to)} · gerado em {fmtDateTime(weekly.generated_at)} por {weekly.generated_by_name}</>
                : <>Gere um relatório narrativo da semana anterior sob demanda (não automático, para economizar créditos).</>}
            </p>
          </div>
          <div className="flex gap-2">
            {weekly ? (
              <Button size="sm" variant="outline" onClick={() => generate(true)} disabled={loading} data-testid="btn-regen-weekly">
                {loading
                  ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Gerando…</>
                  : <><RefreshCw className="h-4 w-4 mr-1" /> Gerar novamente</>}
              </Button>
            ) : (
              <Button size="sm" onClick={() => generate(false)} disabled={loading} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-gen-weekly">
                {loading
                  ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Gerando…</>
                  : <><Sparkles className="h-4 w-4 mr-1" /> Gerar resumo semanal</>}
              </Button>
            )}
          </div>
        </div>
        {weekly && (
          <div className="mt-5 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="border border-slate-200 rounded-md p-3">
                <div className="text-xs uppercase tracking-wider text-slate-500">Executadas</div>
                <div className="font-display text-xl font-bold text-slate-900">{weekly.stats?.total_cases ?? 0}</div>
              </div>
              <div className="border border-slate-200 rounded-md p-3">
                <div className="text-xs uppercase tracking-wider text-slate-500">Suspensões</div>
                <div className="font-display text-xl font-bold text-amber-700">{weekly.stats?.total_suspended ?? 0}</div>
              </div>
              <div className="border border-slate-200 rounded-md p-3">
                <div className="text-xs uppercase tracking-wider text-slate-500">Faturado</div>
                <div className="font-display text-xl font-bold text-slate-900">{safeBRL(weekly.stats?.financials?.billed)}</div>
              </div>
              <div className="border border-slate-200 rounded-md p-3">
                <div className="text-xs uppercase tracking-wider text-slate-500">Recebido</div>
                <div className="font-display text-xl font-bold text-emerald-700">{safeBRL(weekly.stats?.financials?.received)}</div>
              </div>
            </div>
            <div className="border-l-2 border-blue-500 bg-blue-50/40 pl-4 py-3 pr-3 rounded-r text-slate-700 text-sm leading-relaxed whitespace-pre-wrap" data-testid="weekly-narrative">
              {weekly.narrative}
            </div>
            <div className="text-[11px] text-slate-400">
              Gerado por {weekly.model} · para reuniões de diretoria, não substitui os relatórios oficiais.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
